import { type MaybeRefOrGetter, ref, toValue } from "vue";

import i18n from "@/plugins/i18n";
import { API } from "@/api/api";
import { OrderStatus } from "@/constants/order";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useBaseView } from "@/composables/useBaseView";
import { useError } from "@/composables/useError";
import { useSnackbarStore } from "@/store/snackbar.store";

import type { PatchOrderDto } from "@/api/orders/dto/order.dto";
import type { ChangeOrderStatusResult } from "@/types/order";
import type { DisableRule } from "@/types/validation";

const t = i18n.global.t;

export function useChangeOrderStatus() {
  const api = new API();
  const { getAPIErrorMessage } = useError();

  const isSaving = ref(false);

  function createDisableChangeOrderStatusRules(orderStatus: MaybeRefOrGetter<OrderStatus | undefined>): DisableRule[] {
    const { hasAccess } = useBaseView();
    const status = toValue(orderStatus);

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.OrdersEdit),
        reasons: [t("permission.edit-order")],
      },
      {
        disabled: status === OrderStatus.inProgress,
        reasons: [t("orders.change-status-active-travel-order")],
      },
      {
        disabled: status === OrderStatus.completed,
        reasons: [t("orders.change-status-undo-finish")],
      },
    ];
  }

  async function uploadTonuFile(orderId: string, file: File): Promise<boolean> {
    const { showSnackbarError } = useSnackbarStore();

    isSaving.value = true;

    try {
      const response = await api.files.uploadFile("orders", file, [`order:${orderId}`, OrderStatus.tonu]);

      if (!response.success) {
        showSnackbarError(t("orders.tonu-file-upload-error"));
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      showSnackbarError(t("orders.tonu-file-upload-error"));
      return false;
    } finally {
      isSaving.value = false;
    }
  }

  async function patchOrderStatus(orderId: string, data: PatchOrderDto): Promise<ChangeOrderStatusResult> {
    const { showSnackbarError } = useSnackbarStore();

    isSaving.value = true;

    try {
      const response = await api.orders.patchOrder(orderId, data);

      if (response.success) {
        return { success: true, rejectionMessage: null };
      }

      const isRejected = response.status !== null && response.status < 500;
      const rejectionMessage = isRejected ? getAPIErrorMessage(response) : null;

      if (!rejectionMessage) {
        showSnackbarError(t("orders.changes-not-saved"));
      }

      return { success: false, rejectionMessage };
    } catch (error) {
      console.error(error);
      showSnackbarError(t("orders.changes-not-saved"));
      return { success: false, rejectionMessage: null };
    } finally {
      isSaving.value = false;
    }
  }

  return {
    isSaving,
    createDisableChangeOrderStatusRules,
    uploadTonuFile,
    patchOrderStatus,
  };
}
